import type { Theme } from '@mui/material'
import { Box, CardContent, Divider, Grid2 as Grid, Typography } from '@mui/material'
import Card from 'components/@extended/Card'
import Paper from 'components/@extended/Paper'
import VariableCompensationChart from 'components/VariableCompensationChart'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useEffect, useState } from 'react'

dayjs.extend(utc)
import type { ExpandedCompensationPlan } from 'types/comp-plan'
import type { MetricReductionResult } from 'types/components'
import {
  ComponentSourceType,
  ComponentType,
  CompositeFunction,
  CompositeType,
  getCompositeFunction,
  mergeCompositeTargetIntoMetric,
  parseCompositeTargetValue,
  reduceMetricValue,
} from 'types/components'
import type { Period } from 'types/periods'
import { createIdToPeriodFromList } from 'types/periods'
import type { User } from 'types/user'
import { analytics } from 'utils/analytics'
import { useCreateEngagement } from 'hooks/useEngagements'
import ProgressOverview from './ProgressOverview'
import TargetRow from './TargetRow'
import VariableEarnedProgress from './VariableEarnedProgress'
import useAuth from 'hooks/useAuth'
import { getRootOrganizationId } from 'types/user-profile'

interface EmployeeDashboardProps {
  user: User
  theme: Theme
  status: number
  // Should contain ALL plans for the root period (e.g., H1, H2 or Q1, Q2, Q3, Q4) to show complete annual progress
  plan: ExpandedCompensationPlan[]
  // The currently selected plan for detailed view in main content
  selectedPlan?: ExpandedCompensationPlan
}

const EmployeeDashboard: React.FC<EmployeeDashboardProps> = ({ user, theme, status, plan, selectedPlan }) => {
  const { profile, token } = useAuth()
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  const [periodMap, setPeriodMap] = useState<Record<string, Period>>({})
  const { createEngagement } = useCreateEngagement(token || '')

  // Helper function to calculate plan proration based on validity dates
  // Calculate plan validity proration for a specific period
  const calculatePlanValidityProration = (planItem: ExpandedCompensationPlan, periodId: string): number => {
    const period = periodMap[periodId]
    if (!period) return 1

    const periodStart = dayjs.utc(period.startDate)
    const periodEnd = dayjs.utc(period.endDate)
    const totalPeriodDays = periodEnd.diff(periodStart, 'day') + 1

    // If plan has no validity dates, assume it's valid for the entire period
    if (!planItem.plan.effectiveDate && !planItem.plan.endDate) {
      return 1
    }

    // For backward compatibility: use period dates if plan dates are missing
    const planStartDate = planItem.plan.effectiveDate ? dayjs.utc(planItem.plan.effectiveDate) : periodStart
    const planEndDate = planItem.plan.endDate ? dayjs.utc(planItem.plan.endDate) : periodEnd

    // Calculate the overlap between plan validity and this specific period
    const effectiveStart = planStartDate.isAfter(periodStart) ? planStartDate : periodStart
    const effectiveEnd = planEndDate.isBefore(periodEnd) ? planEndDate : periodEnd

    // If there's no overlap, return 0
    if (effectiveEnd.isBefore(effectiveStart)) {
      return 0
    }

    // Calculate how many days the plan was valid in this period
    const planValidDays = effectiveEnd.diff(effectiveStart, 'day') + 1

    // Return the validity proration for this period
    return planValidDays / totalPeriodDays
  }

  // Calculate combined proration for a specific row (period proration * validity proration)
  const calculateRowProration = (planItem: ExpandedCompensationPlan, periodId: string): number => {
    const validityProration = calculatePlanValidityProration(planItem, periodId)
    return planItem.plan.periodProration * validityProration
  }

  // Set initial selected periods when selectedPlan is loaded (for main content)
  useEffect(() => {
    if (selectedPlan) {
      // Get all period IDs from the selected plan for main content
      const planPeriodMap = createIdToPeriodFromList([selectedPlan.period])
      const planPeriodIds = Object.keys(planPeriodMap)
      setSelectedPeriods(planPeriodIds)
      setPeriodMap(planPeriodMap)
    } else if (plan && plan.length > 0) {
      // Fallback to first plan if no plan selected
      const firstPlan = plan[0]
      const planPeriodMap = createIdToPeriodFromList([firstPlan.period])
      const planPeriodIds = Object.keys(planPeriodMap)
      setSelectedPeriods(planPeriodIds)
      setPeriodMap(planPeriodMap)
    }
  }, [selectedPlan, plan])

  // Track employee dashboard view
  useEffect(() => {
    if (plan && plan.length > 0 && status !== 400) {
      if (profile) {
        const rootOrgId = getRootOrganizationId(profile)
        createEngagement({
          userId: profile.user.id,
          rootOrganizationId: rootOrgId,
          eventType: 'incentive_dashboard_view',
          viewedRecordId: selectedPlan?.plan.id || null,
          viewedRecordUserId: user.id,
          viewedRecordType: 'compensation_plan',
          eventData: null,
        })
      }
      analytics.track('employee_incentive_dashboard_viewed', {
        userId: profile?.user.id,
        viewedForUserId: user?.id,
      })
    }
  }, [plan, status, user?.id])

  const displayPlan = selectedPlan || (plan && plan.length > 0 ? plan[0] : undefined)
  if (!plan || plan.length === 0 || !displayPlan || status === 400) {
    return (
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="h5">No valid compensation plan found</Typography>
            <Typography variant="body1">Please contact your manager or HR department for assistance.</Typography>
          </CardContent>
        </Card>
      </Grid>
    )
  }

  const getCompositeTypeFromSourceInfo = (sourceInfo: unknown): CompositeType => {
    if (!sourceInfo) {
      return CompositeType.COMPOSITE_TYPE_SIMPLE
    }

    let parsedSourceInfo = sourceInfo
    if (typeof parsedSourceInfo === 'string') {
      try {
        parsedSourceInfo = JSON.parse(parsedSourceInfo)
      } catch {
        return CompositeType.COMPOSITE_TYPE_SIMPLE
      }
    }

    if (
      parsedSourceInfo &&
      typeof parsedSourceInfo === 'object' &&
      (parsedSourceInfo as { compositeType?: string }).compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX
    ) {
      return CompositeType.COMPOSITE_TYPE_COMPLEX
    }

    return CompositeType.COMPOSITE_TYPE_SIMPLE
  }

  const getTargetReducedValue = (_targetComponentId: string, targetValue: string): number => {
    // For composite targets, extract the manually-set overall value.
    // The composite function is only applied to actuals/metrics, never to target rows.
    const parsedTarget = parseCompositeTargetValue(targetValue)
    if (parsedTarget?.value !== undefined) {
      const parsedOverallValue = Number.parseFloat(parsedTarget.value)
      return Number.isFinite(parsedOverallValue) ? parsedOverallValue : 0
    }

    // Non-composite or non-JSON target
    const parsedTargetValue = Number.parseFloat(targetValue)
    return Number.isFinite(parsedTargetValue) ? parsedTargetValue : 0
  }

  const findMetricValue = (
    targetComponentId: string,
    targetPeriodId: string,
    targetValue?: string
  ): MetricReductionResult => {
    if (!displayPlan) {
      return {
        functionUsed: CompositeFunction.COMPOSITE_FUNCTION_NONE,
        rawValue: '',
        reducedValue: 0,
      }
    }

    // Use the selected/display plan for metric values (main content)
    let metric = null
    let comp = null

    if (displayPlan.metrics) {
      metric = displayPlan.metrics.find((m) => m.componentId === targetComponentId && m.periodId === targetPeriodId)
      if (metric) {
        comp = displayPlan.components.find((c) => c.id === targetComponentId)
      }
    }

    if (metric && comp && metric.value) {
      const compositeType = getCompositeTypeFromSourceInfo(comp.sourceInfo)
      const isCompositeComponent = comp.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
      const isComplexComposite = compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX

      if (isCompositeComponent && isComplexComposite && targetValue) {
        const parsedTarget = parseCompositeTargetValue(targetValue)
        const hasCompositeTargetRows =
          parsedTarget?.compositeTargetValues &&
          Array.isArray(parsedTarget.compositeTargetValues) &&
          parsedTarget.compositeTargetValues.length > 0

        if (hasCompositeTargetRows) {
          const mergedMetricValue = mergeCompositeTargetIntoMetric(
            metric.value,
            parsedTarget,
            comp.componentType as ComponentType
          )

          const metricWithMergedTargets = {
            ...metric,
            value: JSON.stringify(mergedMetricValue),
          }

          return reduceMetricValue(metricWithMergedTargets, comp, CompositeType.COMPOSITE_TYPE_COMPLEX)
        }
      }

      return reduceMetricValue(metric, comp, compositeType)
    }

    return {
      functionUsed: CompositeFunction.COMPOSITE_FUNCTION_NONE,
      rawValue: '',
      reducedValue: 0,
    }
  }

  const calculateProgress = (
    targetComponentId: string,
    targetValue: string,
    targetPeriodId: string,
    lowerIsBetter = false
  ): number => {
    const metricValue = findMetricValue(targetComponentId, targetPeriodId, targetValue)

    const safeTargetValue = getTargetReducedValue(targetComponentId, targetValue)

    let progress: number
    if (lowerIsBetter) {
      if (metricValue.reducedValue <= 0 || safeTargetValue <= 0) {
        progress = 0
      } else {
        progress = (safeTargetValue / metricValue.reducedValue) * 100
      }
    } else {
      if (safeTargetValue <= 0) {
        progress = 0
      } else {
        progress = (metricValue.reducedValue / safeTargetValue) * 100
      }
    }

    return Number.isFinite(progress) ? progress : 0
  }

  return !displayPlan ? (
    <></>
  ) : (
    <Grid container spacing={2}>
      {/* Main content */}
      <Grid size={{ xs: 12, md: 9 }}>
        <ProgressOverview
          key="progress-overview"
          plan={displayPlan}
          selectedPeriods={selectedPeriods}
          periodMap={periodMap}
        />

        {/* Add Table Summaries for selected plan */}
        {displayPlan.plan.variableAllocations
          .filter((table) => {
            // Only show tables that have at least one row matching selected periods
            return table.rows.some((row) => selectedPeriods.includes(row.periodId as string))
          })
          .map((table) => (
            <Paper key={table.id} sx={{ mb: 2, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                {/* Header Row - Hide on mobile, show on tablet+ */}
                <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Component
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {table.label}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Variable Compensation Earnings
                  </Typography>
                </Grid>

                {/* Mobile Header - Show only on mobile */}
                <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {table.label}
                  </Typography>
                </Grid>

                {/* Divider */}
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                {/* Table Rows */}
                {table.rows.map((row) => {
                  const progressEntry = displayPlan.progress?.find(
                    (accrual) => accrual.allocationRowId === row.id && accrual.allocationTableId === table.id
                  ) || { proratedPayoutAmount: 0 }

                  // Use ledger if period is closed and ledger exists, otherwise use progress
                  const amount = progressEntry.proratedPayoutAmount

                  const onTargetPayout =
                    displayPlan.plan.baseSalary * displayPlan.plan.variablePercent * row.allocationPercentage

                  // Handle multiple components per row
                  const componentRows = (row.componentIds || [])
                    .map((componentId) => {
                      const target = displayPlan.targets.find(
                        (t) => t.componentId === componentId && t.periodId === row.periodId
                      )
                      // Skip if target is not found
                      if (!target) return null

                      const component = displayPlan.components.find((c) => c.id === componentId)
                      const componentType = component?.componentType || 'float'

                      const progress = calculateProgress(
                        target.componentId || '',
                        target.value || '',
                        target.periodId || '',
                        component?.lowerIsBetter || false
                      )

                      const compositeType = getCompositeTypeFromSourceInfo(component?.sourceInfo)
                      const compositeFunction = getCompositeFunction(component?.sourceInfo)

                      const metric = displayPlan.metrics?.find(
                        (m) => m.componentId === componentId && m.periodId === row.periodId
                      )

                      return {
                        componentId,
                        label: component?.label || '',
                        target,
                        progress,
                        componentType,
                        compositeType,
                        compositeFunction,
                        lowerIsBetter: component?.lowerIsBetter || false,
                        currency: 'USD', // You may want to get this from component or plan
                        currencyDecimals: 0, // You may want to get this from component
                        numericDecimalPlaces: component?.numericDecimalPlaces,
                        precision: component?.precision,
                        effectiveDate: metric?.effectiveDate,
                      }
                    })
                    .filter(Boolean) // Remove null entries

                  // Skip rendering if no valid component rows
                  if (componentRows.length === 0) return null

                  const rowLableMargin = componentRows.length > 1 ? 4 : 1
                  const variableEarnedProgressMargin = componentRows.length > 1 ? 4.4 : 2

                  return (
                    <React.Fragment key={row.id}>
                      {componentRows
                        .filter((row) => row !== null)
                        .map((componentRow, index) => (
                          <React.Fragment key={`${row.id}-${componentRow.componentId}`}>
                            {/* Desktop Layout - Hidden on mobile */}
                            <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: rowLableMargin }}>
                                {index === 0 ? row.label : ''}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                              <Box sx={{ mt: 2, pr: 2 }}>
                                <TargetRow
                                  target={componentRow.target}
                                  findMetricValue={findMetricValue}
                                  calculateProgress={() => componentRow.progress}
                                  theme={theme}
                                  componentType={componentRow.componentType}
                                  compositeType={componentRow.compositeType}
                                  compositeFunction={componentRow.compositeFunction}
                                  lowerIsBetter={componentRow.lowerIsBetter}
                                  hideLabel
                                  targetLabel={
                                    componentRows.length > 1
                                      ? `${periodMap[row.periodId as string]?.label || ''} - ${componentRow.label}`
                                      : undefined
                                  }
                                  periodLabel={periodMap[row.periodId as string]?.label || ''}
                                  currency={componentRow.currency}
                                  currencyDecimals={componentRow.currencyDecimals}
                                  numericDecimalPlaces={componentRow.numericDecimalPlaces}
                                  precision={componentRow.precision}
                                  effectiveDate={componentRow.effectiveDate}
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }} sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {index === 0 && amount != null && (
                                <Box sx={{ mt: variableEarnedProgressMargin, pr: 2 }}>
                                  <VariableEarnedProgress
                                    earnedAmount={amount}
                                    targetAmount={onTargetPayout * calculateRowProration(displayPlan, row.periodId!)}
                                    theme={theme}
                                    hideLabel
                                  />
                                </Box>
                              )}
                            </Grid>

                            {/* Mobile Layout - Stacked */}
                            <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {index === 0 && (
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                                  {row.label}
                                </Typography>
                              )}
                              <Box sx={{ mb: 2 }}>
                                <TargetRow
                                  target={componentRow.target}
                                  findMetricValue={findMetricValue}
                                  calculateProgress={() => componentRow.progress}
                                  theme={theme}
                                  componentType={componentRow.componentType}
                                  compositeType={componentRow.compositeType}
                                  compositeFunction={componentRow.compositeFunction}
                                  lowerIsBetter={componentRow.lowerIsBetter}
                                  hideLabel={true}
                                  targetLabel={
                                    componentRows.length > 1
                                      ? `${periodMap[row.periodId as string]?.label || ''} - ${componentRow.label}`
                                      : undefined
                                  }
                                  periodLabel={periodMap[row.periodId as string]?.label || ''}
                                  currency={componentRow.currency}
                                  currencyDecimals={componentRow.currencyDecimals}
                                  numericDecimalPlaces={componentRow.numericDecimalPlaces}
                                  precision={componentRow.precision}
                                  effectiveDate={componentRow.effectiveDate}
                                />
                              </Box>
                              {index === 0 && amount != null && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                    Variable Compensation Earnings
                                  </Typography>
                                  <VariableEarnedProgress
                                    earnedAmount={amount}
                                    targetAmount={onTargetPayout * calculateRowProration(displayPlan, row.periodId!)}
                                    theme={theme}
                                    hideLabel
                                  />
                                </Box>
                              )}
                            </Grid>
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  )
                })}
              </Grid>
            </Paper>
          ))}
      </Grid>

      {/* Right sidebar */}
      <Grid size={{ xs: 12, md: 3 }}>
        {(() => {
          if (!displayPlan || !plan || plan.length === 0) {
            return <div>No data available</div>
          }

          // Filter plans to only include those with the same rootPeriodId as the selected/display plan
          const selectedRootPeriodId = displayPlan.period?.rootPeriodId || displayPlan.period?.id
          const filteredPlans = plan.filter(
            (planItem) =>
              planItem?.period?.rootPeriodId === selectedRootPeriodId || planItem?.period?.id === selectedRootPeriodId
          )

          // Build progress data for chart
          const progressData: { label: string; earned: number; remaining: number; overage: number; total: number }[] =
            []

          filteredPlans.forEach((planItem) => {
            const baseSalary = planItem.plan.baseSalary || 0
            const variablePercent = planItem.plan.variablePercent || 0

            // Get period label for this plan
            const planPeriodMap = createIdToPeriodFromList([planItem.period])
            const periodLabel = planPeriodMap[planItem.plan.periodId]?.label || 'Unknown'

            // Collect progress data for each allocation row
            planItem.plan.variableAllocations.forEach((table) => {
              table.rows
                .filter((row) => !row.disabled && (row.allocationPercentage || 0) > 0)
                .forEach((row) => {
                  const rowTarget = baseSalary * variablePercent * (row.allocationPercentage || 0)
                  const rowProration = calculateRowProration(planItem, row.periodId!)
                  const proratedTarget = rowTarget * rowProration

                  // Find progress entry for this row
                  const progressEntry = planItem.progress?.find(
                    (accrual) => accrual.allocationRowId === row.id && accrual.allocationTableId === table.id
                  )
                  const earned = progressEntry?.proratedPayoutAmount || 0

                  const remaining = Math.max(0, proratedTarget - earned)
                  const overage = Math.max(0, earned - proratedTarget)

                  progressData.push({
                    label: `${periodLabel} - ${row.label}`,
                    earned: Math.min(earned, proratedTarget),
                    remaining,
                    overage,
                    total: proratedTarget,
                  })
                })
            })
          })

          // Calculate legend items (each progressData item can have up to 3: earned, remaining, overage)
          const legendItemCount = progressData.reduce((count, item) => {
            let items = 0
            if (item.earned > 0) items++
            if (item.remaining > 0) items++
            if (item.overage > 0) items++
            return count + items
          }, 0)

          // Base chart height + estimated legend height (roughly 24px per row, 2 items per row)
          const chartHeight = 380
          const legendRows = Math.ceil(legendItemCount / 2)
          const legendHeight = Math.max(60, legendRows * 24)
          const cardMinHeight = chartHeight + legendHeight + 20 // 20 for title/padding

          return (
            <Paper sx={{ p: 3, minHeight: cardMinHeight }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Annual Variable Compensation
              </Typography>
              <Box sx={{ width: '100%' }}>
                <VariableCompensationChart
                  data={progressData}
                  theme={theme}
                  currency="USD"
                  currencyDecimals={0}
                  height={chartHeight}
                />
              </Box>
            </Paper>
          )
        })()}
      </Grid>
    </Grid>
  )
}

export default EmployeeDashboard
